import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import PptxGenJS from 'pptxgenjs';

// Define slide types and interfaces
export interface SlideSection {
  id: string;
  type: 'slide';
  content: string;
  title?: string;
  background?: string;
  style?: SlideStyle;
}

interface SlideStyle {
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: number;
  alignment: 'left' | 'center' | 'right';
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  backgroundImage?: string;
  padding?: number;
  lineHeight?: number;
  margin?: number;
}

const defaultSlideStyle: SlideStyle = {
  backgroundColor: '#FFFFFF',
  textColor: '#1C2B39',
  fontFamily: 'Arial',
  fontSize: 24,
  alignment: 'center',
  bold: false,
  italic: false,
  underline: false,
  backgroundImage: '',
  padding: 20,
  lineHeight: 1.5,
  margin: 20
};

// Function to convert HTML content to plain text
function htmlToText(html: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
}

// Function to extract slides from sermon content
export function extractSlidesFromContent(content: string): SlideSection[] {
  try {
    console.log(`Extracting slides from content (${content.length} characters)`);
    console.log('Content preview:', content.substring(0, 500));

    // Parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    // Find all sermon sections marked as slides
    const slideElements = Array.from(doc.querySelectorAll('.sermon-section[data-is-slide="true"]'));

    console.log(`Found ${slideElements.length} slide elements`);

    if (slideElements.length === 0) {
      console.warn('No slides found in content - looking for all sermon sections instead');

      // Fallback: try to find any sermon sections
      const allSections = Array.from(doc.querySelectorAll('.sermon-section'));
      console.log(`Found ${allSections.length} total sermon sections`);

      if (allSections.length === 0) {
        console.warn('No sermon sections found at all');
        return [];
      }

      // Return empty array if no marked slides, let the user mark them
      return [];
    }

    // Create slide sections from marked sermon sections
    return slideElements.map((element, index) => {
      console.log(`Processing slide element ${index + 1}:`, element);

      // Get the content text - try multiple selectors and methods
      let slideContent = '';

      // Method 1: Try to get text content directly
      slideContent = element.textContent || element.innerText || '';

      // Method 2: If that fails, try specific selectors
      if (!slideContent.trim()) {
        const contentElement = element.querySelector('div > div:last-child') ||
                              element.querySelector('p') ||
                              element.querySelector('div') ||
                              element;

        slideContent = contentElement.textContent ||
                      contentElement.innerText ||
                      contentElement.innerHTML?.replace(/<[^>]*>/g, '') ||
                      '';
      }

      // Method 3: If still no content, try innerHTML stripping
      if (!slideContent.trim()) {
        slideContent = element.innerHTML?.replace(/<[^>]*>/g, '') || '';
      }

      // Clean up the content
      slideContent = slideContent
        .replace(/\s+/g, ' ') // Normalize whitespace
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .trim();

      // Final fallback
      if (!slideContent || slideContent.length === 0) {
        slideContent = `Slide ${index + 1}`;
        console.warn(`Slide ${index + 1} has no content, using fallback`);
      }

      console.log(`Slide ${index + 1} content extracted: "${slideContent.substring(0, 100)}..." (${slideContent.length} chars)`);

      // Get the section type
      const sectionClass = Array.from(element.classList)
        .find(cls => cls.startsWith('sermon-section-'))
        ?.replace('sermon-section-', '');

      // Get point number for main points
      const pointNumber = element.getAttribute('data-point-number');

      // Get attribution for quotes
      const attribution = element.getAttribute('data-attribution');

      // Create a title based on section type and attributes
      let title = '';
      if (sectionClass) {
        title = sectionClass.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        if (sectionClass === 'main-point' && pointNumber) {
          title += ` ${pointNumber}`;
        } else if (sectionClass === 'quote' && attribution) {
          title += ` - ${attribution}`;
        }
      }

      console.log(`Slide ${index + 1} processed:`, {
        title,
        contentLength: slideContent.length,
        sectionClass,
        pointNumber,
        attribution
      });

      const slide: SlideSection = {
        id: `slide-${index}`,
        type: 'slide' as const,
        content: slideContent,
        title,
        style: {
          backgroundColor: '#FFFFFF',
          textColor: '#1C2B39',
          fontFamily: 'Arial',
          fontSize: 24,
          alignment: 'center' as const,
          bold: false,
          italic: false,
          underline: false,
          backgroundImage: '',
          padding: 20,
          lineHeight: 1.5,
          margin: 20
        }
      };

      return slide;
    });
  } catch (error) {
    console.error('Error extracting slides from content:', error);
    console.error('Error stack:', error.stack);
    return [];
  }
}

// Function to split content into slides
export function splitContentIntoSlides(content: string, maxCharsPerSlide: number = 200): string[] {
  const text = htmlToText(content);
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];

  const slides: string[] = [];
  let currentSlide = '';

  for (const sentence of sentences) {
    if (currentSlide.length + sentence.length > maxCharsPerSlide && currentSlide.length > 0) {
      slides.push(currentSlide.trim());
      currentSlide = sentence;
    } else {
      currentSlide += sentence;
    }
  }

  if (currentSlide.length > 0) {
    slides.push(currentSlide.trim());
  }

  return slides;
}

// Map font family names to PowerPoint-compatible fonts
function mapFontFamily(fontFamily: string): string {
  // Extract the primary font name from font stacks
  const primaryFont = fontFamily.split(',')[0].trim().replace(/['"`]/g, '');

  // Map of web fonts to PowerPoint-compatible fonts
  const fontMap: Record<string, string> = {
    'Open Sans': 'Arial',
    'Roboto': 'Arial',
    'Lato': 'Arial',
    'Montserrat': 'Arial',
    'Oswald': 'Arial',
    'Raleway': 'Arial',
    'Merriweather': 'Times New Roman',
    'Playfair Display': 'Times New Roman',
    'Ubuntu': 'Arial',
    'Poppins': 'Arial'
  };

  return fontMap[primaryFont] || primaryFont;
}

// Function to create PowerPoint presentation using PptxGenJS
async function createPowerPoint(
  slideSections: SlideSection[],
  title: string
): Promise<Blob> {
  // Create a new presentation
  const pptx = new PptxGenJS();

  // Set presentation properties
  pptx.author = 'SermonBuild';
  pptx.company = 'SermonBuild';
  pptx.subject = title;
  pptx.title = title;

  // Add slides - use for loop instead of forEach to handle async operations
  for (let index = 0; index < slideSections.length; index++) {
    const section = slideSections[index];

    // Create a new slide
    const slide = pptx.addSlide();

    // Set background - try to use uploaded images, fallback to color for external URLs
    if (section.style?.backgroundImage) {
      try {
        // Check if it's a Supabase storage URL (our uploaded images)
        if (section.style.backgroundImage && (section.style.backgroundImage.includes('supabase') || section.style.backgroundImage.startsWith('http'))) {
          console.log('Processing background image:', section.style.backgroundImage);
          const base64Image = await downloadImageAsBase64(section.style.backgroundImage);
          if (base64Image) {
            console.log('Successfully converted image to base64');
            slide.background = { data: base64Image };
          } else {
            console.warn('Could not download background image, using color instead');
            slide.background = { color: section.style?.backgroundColor || '#FFFFFF' };
          }
        } else {
          // External URL - use color to avoid CORS issues
          slide.background = { color: section.style?.backgroundColor || '#FFFFFF' };
          console.log(`Note: External image URL not included in PowerPoint export. Use uploaded images for full background support.`);
        }
      } catch (error) {
        console.warn('Error setting background image:', error);
        slide.background = { color: section.style?.backgroundColor || '#FFFFFF' };
      }
    } else {
      slide.background = { color: section.style?.backgroundColor || '#FFFFFF' };
    }

    // Map font family to PowerPoint-compatible font
    const fontFace = mapFontFamily(section.style?.fontFamily || 'Arial');

    // Add content - centered both horizontally and vertically
    // Use a single text box that fills most of the slide for true vertical centering
    const textContent = section.title 
      ? `${section.title}\n\n${section.content}`
      : section.content;
    
    slide.addText(textContent, {
      x: 0.5,
      y: 0.5,
      w: '90%',
      h: '85%',
      fontSize: section.style?.fontSize || 24,
      fontFace: fontFace,
      color: section.style?.textColor || '#1C2B39',
      bold: section.style?.bold || false,
      italic: section.style?.italic || false,
      underline: section.style?.underline || false,
      align: section.style?.alignment || 'center',
      valign: 'middle',
      margin: section.style?.margin || 20,
      lineSpacingMultiple: (section.style?.lineHeight || 1.5)
    });
  }

  // Generate the PowerPoint file
  return await pptx.write('blob');
}

// Function to create ProPresenter 7 presentation
async function createProPresenter(
  slideSections: SlideSection[],
  title: string
): Promise<Blob> {
  try {
    // Validate input
    if (!slideSections || slideSections.length === 0) {
      console.error('ProPresenter 7 export failed: No slides provided');
      throw new Error('No slides provided for ProPresenter 7 export');
    }

    if (!title || title.trim() === '') {
      title = 'Untitled Presentation';
    }

    console.log(`Creating ProPresenter 7 presentation with ${slideSections.length} slides`);
    console.log('Slide sections input:', slideSections.map((s, i) => ({
      index: i,
      title: s.title,
      contentPreview: s.content?.substring(0, 50) + '...',
      contentLength: s.content?.length
    })));

    // Create presentation UUID and timestamp
    const presentationUuid = generateUuid();
    const groupUuid = generateUuid();
    const documentCreationDate = new Date().toISOString();
    const validSlides: string[] = [];

    // Process each slide with improved error handling
    for (let index = 0; index < slideSections.length; index++) {
      const slide = slideSections[index];

      try {
        console.log(`Processing slide ${index + 1}/${slideSections.length}:`, {
          title: slide.title,
          contentLength: slide.content?.length,
          hasContent: !!slide.content
        });

        const slideUuid = generateUuid();
        const textElementUuid = generateUuid();
        const backgroundColor = hexToRgba(slide.style?.backgroundColor || '#FFFFFF');

        // Extract and clean content with better fallbacks
        let slideContent = '';

        if (slide.content && slide.content.trim()) {
          slideContent = slide.content.trim();

          // Clean HTML if present
          if (slideContent.includes('<') || slideContent.includes('&')) {
            try {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = slideContent;
              const extractedText = tempDiv.textContent || tempDiv.innerText || '';
              if (extractedText && extractedText.trim().length > 0) {
                slideContent = extractedText;
              }
            } catch (htmlError) {
              console.warn('HTML cleaning failed, using raw content');
              slideContent = slideContent.replace(/<[^>]*>/g, '');
            }
          }

          // Clean up entities and whitespace
          slideContent = slideContent
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/\s+/g, ' ')
            .trim();
        }

        // Final fallback if no content
        if (!slideContent || slideContent.length === 0) {
          slideContent = slide.title || `Slide ${index + 1}`;
        }

        // Combine title and content if both exist
        let finalSlideText = slideContent;
        if (slide.title && slide.title.trim() && slide.title.trim() !== slideContent.trim()) {
          finalSlideText = `${slide.title.trim()}\n\n${slideContent}`;
        }

        console.log(`Slide ${index + 1} final text (${finalSlideText.length} chars): "${finalSlideText.substring(0, 100)}..."`);

        // Create RTF data with debugging
        const rtfData = createProPresenterRTFData(
          finalSlideText,
          slide.style?.textColor || '#1C2B39',
          slide.style?.fontFamily || 'Arial',
          slide.style?.fontSize || 24,
          getAlignmentValue(slide.style?.alignment || 'center'),
          slide.style?.lineHeight || 1.5
        );

        if (!rtfData || rtfData.length < 10) {
          console.error(`Invalid RTF data generated for slide ${index + 1}:`, rtfData);
          throw new Error(`Failed to generate RTF data for slide ${index + 1}`);
        }

        console.log(`RTF data created for slide ${index + 1}: ${rtfData.length} chars`);

        const slideLabel = escapeXml(slide.title || `Slide ${index + 1}`);

        // Create the slide XML with proper ProPresenter 7 structure
        const slideXml = `    <RVDisplaySlide backgroundColor="${backgroundColor}" enabled="1" highlightColor="0 0 0 0" hotKey="" label="${slideLabel}" notes="" slideType="1" sort_index="${index}" UUID="${slideUuid}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="${index}">
      <cues rvXMLIvarName="cues"></cues>
      <displayElements rvXMLIvarName="displayElements">
        <RVTextElement displayDelay="0" displayName="Default" locked="0" persistent="0" typeID="0" fromTemplate="0" bezelRadius="0" drawingFill="0" drawingShadow="0" drawingStroke="0" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="0" verticalAlignment="1" RTFData="${rtfData}" revealType="0" UUID="${textElementUuid}" serialization-array-index="0">
          <stroke rvXMLIvarName="stroke">
            <RVStrokeItem strokeColor="0 0 0 1" strokeWidth="1"></RVStrokeItem>
          </stroke>
          <shadow rvXMLIvarName="shadow">
            <RVShadowItem shadowColor="0 0 0 0.5" shadowOffset="{0, 0}" shadowBlurRadius="0" shadowAngle="0"></RVShadowItem>
          </shadow>
          <position rvXMLIvarName="position">
            <RVRect3D x="0.05" y="0.05" z="0" width="0.9" height="0.9"></RVRect3D>
          </position>
        </RVTextElement>
      </displayElements>
      <arrangements rvXMLIvarName="arrangements"></arrangements>
    </RVDisplaySlide>`;

        validSlides.push(slideXml);
        console.log(`Successfully created XML for slide ${index + 1}`);

      } catch (slideError) {
        console.error(`Error processing slide ${index + 1}:`, slideError);

        // Create a guaranteed working fallback slide
        const slideUuid = generateUuid();
        const textElementUuid = generateUuid();
        const fallbackText = `Slide ${index + 1}: ${slide.content?.substring(0, 100) || slide.title || 'Sample Text'}`;

        // Use simple RTF that we know works
        const simpleRtfContent = `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss Arial;}}\\f0\\fs48\\qc ${fallbackText.replace(/[{}\\]/g, '')}\\par}`;
        const rtfData = btoa(simpleRtfContent);

        const fallbackSlideXml = `    <RVDisplaySlide backgroundColor="1 1 1 1" enabled="1" highlightColor="0 0 0 0" hotKey="" label="Slide ${index + 1}" notes="" slideType="1" sort_index="${index}" UUID="${slideUuid}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="${index}">
      <cues rvXMLIvarName="cues"></cues>
      <displayElements rvXMLIvarName="displayElements">
        <RVTextElement displayDelay="0" displayName="Default" locked="0" persistent="0" typeID="0" fromTemplate="0" bezelRadius="0" drawingFill="0" drawingShadow="0" drawingStroke="0" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="0" verticalAlignment="1" RTFData="${rtfData}" revealType="0" UUID="${textElementUuid}" serialization-array-index="0">
          <stroke rvXMLIvarName="stroke">
            <RVStrokeItem strokeColor="0 0 0 1" strokeWidth="1"></RVStrokeItem>
          </stroke>
          <shadow rvXMLIvarName="shadow">
            <RVShadowItem shadowColor="0 0 0 0.5" shadowOffset="{0, 0}" shadowBlurRadius="0" shadowAngle="0"></RVShadowItem>
          </shadow>
          <position rvXMLIvarName="position">
            <RVRect3D x="0.05" y="0.05" z="0" width="0.9" height="0.9"></RVRect3D>
          </position>
        </RVTextElement>
      </displayElements>
      <arrangements rvXMLIvarName="arrangements"></arrangements>
    </RVDisplaySlide>`;

        validSlides.push(fallbackSlideXml);
        console.log(`Created fallback slide for ${index + 1}`);
      }
    }

    // Validate we have slide content
    if (validSlides.length === 0) {
      throw new Error('Failed to generate any valid slide content');
    }

    console.log(`Generated ${validSlides.length} valid slides out of ${slideSections.length} input slides`);

    const slidesXml = validSlides.join('\n');
    const escapedTitle = escapeXml(title);

    // Create the complete ProPresenter 7 document with proper structure
    const proPresenterDocument = `<?xml version="1.0" encoding="UTF-8"?>
<RVPresentationDocument height="1080" width="1920" versionNumber="700" docType="0" creatorCode="1349676880" lastDateUsed="${documentCreationDate}" usedCount="0" category="Sermon" resourcesDirectory="" backgroundColor="0 0 0 1" drawingBackgroundColor="0 0 0 0" notes="" artist="" author="SermonBuild" album="" title="${escapedTitle}" CCLIDisplay="0" CCLIArtistCredits="" CCLISongTitle="" CCLIPublisher="" CCLICopyrightYear="" CCLISongNumber="" CCLILicenseNumber="" chordChartPath="" lastModifiedDate="${documentCreationDate}" UUID="${presentationUuid}">
  <timeline timeOffset="0" selectedMediaTrackIndex="-1" unitOfMeasure="60" duration="0" loop="0" rvXMLIvarName="timeline">
    <timeCues rvXMLIvarName="timeCues"></timeCues>
    <mediaTracks rvXMLIvarName="mediaTracks"></mediaTracks>
  </timeline>
  <groups rvXMLIvarName="groups">
    <RVSlideGrouping name="${escapedTitle}" uuid="${groupUuid}" color="0 0 0 0" serialization-array-index="0">
      <slides rvXMLIvarName="slides">
${slidesXml}
      </slides>
    </RVSlideGrouping>
  </groups>
  <arrangements rvXMLIvarName="arrangements"></arrangements>
</RVPresentationDocument>`;

    console.log(`ProPresenter 7 document created successfully:`);
    console.log(`- Document size: ${proPresenterDocument.length} characters`);
    console.log(`- Number of slides: ${validSlides.length}`);
    console.log(`- Title: ${title}`);
    console.log(`- Sample slide XML preview:`, validSlides[0]?.substring(0, 200) + '...');

    // Validate the document size
    if (proPresenterDocument.length < 2000) {
      console.error('ProPresenter 7 document is suspiciously small:', proPresenterDocument.length);
      console.error('Document content preview:', proPresenterDocument.substring(0, 500));
      throw new Error('Generated ProPresenter 7 document is too small');
    }

    // Additional validation - check for RTF data
    if (!proPresenterDocument.includes('RTFData=')) {
      console.error('ProPresenter 7 document missing RTF data');
      throw new Error('Generated ProPresenter 7 document is missing RTF data');
    }

    // Return as blob
    return new Blob([proPresenterDocument], { type: 'application/xml' });

  } catch (error) {
    console.error('Critical error in createProPresenter:', error);
    console.error('Error stack:', error.stack);

    // Create an absolute minimal working document as final fallback
    const fallbackUuid = generateUuid();
    const fallbackDate = new Date().toISOString();
    const fallbackTitle = escapeXml(title || 'Emergency Fallback');

    const fallbackDocument = `<?xml version="1.0" encoding="UTF-8"?>
<RVPresentationDocument height="1080" width="1920" versionNumber="700" docType="0" creatorCode="1349676880" lastDateUsed="${fallbackDate}" usedCount="0" category="Sermon" resourcesDirectory="" backgroundColor="0 0 0 1" drawingBackgroundColor="0 0 0 0" notes="" artist="" author="SermonBuild" album="" title="${fallbackTitle}" CCLIDisplay="0" CCLIArtistCredits="" CCLISongTitle="" CCLIPublisher="" CCLICopyrightYear="" CCLISongNumber="" CCLILicenseNumber="" chordChartPath="" lastModifiedDate="${fallbackDate}" UUID="${fallbackUuid}">
  <timeline timeOffset="0" selectedMediaTrackIndex="-1" unitOfMeasure="60" duration="0" loop="0" rvXMLIvarName="timeline">
    <timeCues rvXMLIvarName="timeCues"></timeCues>
    <mediaTracks rvXMLIvarName="mediaTracks"></mediaTracks>
  </timeline>
  <groups rvXMLIvarName="groups">
    <RVSlideGrouping name="${fallbackTitle}" uuid="${generateUuid()}" color="0 0 0 0" serialization-array-index="0">
      <slides rvXMLIvarName="slides">
        <RVDisplaySlide backgroundColor="1 1 1 1" enabled="1" highlightColor="0 0 0 0" hotKey="" label="Error Slide" notes="" slideType="1" sort_index="0" UUID="${generateUuid()}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="0">
          <cues rvXMLIvarName="cues"></cues>
          <displayElements rvXMLIvarName="displayElements">
            <RVTextElement displayDelay="0" displayName="Default" locked="0" persistent="0" typeID="0" fromTemplate="0" bezelRadius="0" drawingFill="0" drawingShadow="0" drawingStroke="0" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="0" verticalAlignment="1" RTFData="e1xydGYxXGFuc2lcZGVmZjB7XGZvbnR0Ymx7XGYwXGZzd2lzcyBBcmlhbDt9fVxmMFxmczQ4XHFjIEVycm9yIGdlbmVyYXRpbmcgc2xpZGVzXHBhcn0=" revealType="0" UUID="${generateUuid()}" serialization-array-index="0">
              <stroke rvXMLIvarName="stroke">
                <RVStrokeItem strokeColor="0 0 0 1" strokeWidth="1"></RVStrokeItem>
              </stroke>
              <shadow rvXMLIvarName="shadow">
                <RVShadowItem shadowColor="0 0 0 0.5" shadowOffset="{0, 0}" shadowBlurRadius="0" shadowAngle="0"></RVShadowItem>
              </shadow>
              <position rvXMLIvarName="position">
                <RVRect3D x="0.05" y="0.05" z="0" width="0.9" height="0.9"></RVRect3D>
              </position>
            </RVTextElement>
          </displayElements>
          <arrangements rvXMLIvarName="arrangements"></arrangements>
        </RVDisplaySlide>
      </slides>
    </RVSlideGrouping>
  </groups>
  <arrangements rvXMLIvarName="arrangements"></arrangements>
</RVPresentationDocument>`;

    console.log('Returning fallback document due to error');
    return new Blob([fallbackDocument], { type: 'application/xml' });
  }
}

// Helper function to create ProPresenter slide content
function createProPresenterSlideContent(slide: SlideSection, index: number): string {
  const backgroundColor = slide.style?.backgroundColor || '#FFFFFF';
  const textColor = slide.style?.textColor || '#1C2B39';
  const fontFamily = slide.style?.fontFamily || 'Arial';
  const fontSize = slide.style?.fontSize || 24;
  const alignment = slide.style?.alignment || 'center';
  const content = escapeXml(slide.content);
  const title = escapeXml(slide.title || `Slide ${index + 1}`);
  const lineHeight = slide.style?.lineHeight || 1.5;

  // Convert hex color to RGBA format (0-1 range)
  const bgColorRgba = hexToRgba(backgroundColor);
  const textColorRgba = hexToRgba(textColor);

  // Convert alignment to ProPresenter format
  const alignmentValue = alignment === 'left' ? 0 : alignment === 'right' ? 2 : 1;

  // Create a ProPresenter 6 compatible document
  return `<?xml version="1.0" encoding="UTF-8"?>
<RVPresentationDocument formatVersion="1" versionNumber="600" width="1920" height="1080" usedCount="0" category="Sermon" lastDateUsed="${new Date().toISOString()}" backgroundColor="${bgColorRgba}" drawingBackgroundColor="0 0 0 0" CCLIDisplay="false" lastModifiedDate="${new Date().toISOString()}" selectedArrangementID="" chordChartPath="" os="1" buildNumber="6016">
  <RVTimeline timeOffset="0" duration="0" selectedMediaTrackIndex="-1" loop="false" rvXMLIvarName="timeline">
    <array rvXMLIvarName="timeCues" />
    <array rvXMLIvarName="mediaTracks" />
  </RVTimeline>
  <array rvXMLIvarName="groups">
    <RVSlideGrouping name="${escapeXml(title)}" uuid="${generateUuid()}" color="0 0 0 0" serialization-array-index="0">
      <array rvXMLIvarName="slides">
        <RVDisplaySlide backgroundColor="${bgColorRgba}" enabled="true" highlightColor="0 0 0 0" hotKey="" label="" notes="" slideType="1" sort_index="0" UUID="${generateUuid()}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="0">
          <array rvXMLIvarName="cues" />
          <array rvXMLIvarName="displayElements">
            <RVTextElement displayDelay="0" displayName="Default" locked="false" persistent="false" typeID="0" fromTemplate="false" bezelRadius="0" drawingFill="false" drawingShadow="false" drawingStroke="false" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="false" verticalAlignment="0" RTFData="${createRTFData(content, textColor, fontFamily, fontSize, alignmentValue, lineHeight)}" revealType="0" serialization-array-index="0">
              <RVRect3D rvXMLIvarName="position">${createPositionRect()}</RVRect3D>
              <shadow rvXMLIvarName="shadow">0 0 0 0 0 0 0 0</shadow>
              <dictionary rvXMLIvarName="stroke">
                <NSColor rvXMLDictionaryKey="RVShapeElementStrokeColorKey">0 0 0 1</NSColor>
                <NSNumber rvXMLDictionaryKey="RVShapeElementStrokeWidthKey" hint="double">1</NSNumber>
              </dictionary>
            </RVTextElement>
          </array>
          <array rvXMLIvarName="arrangements" />
        </RVDisplaySlide>
      </array>
    </RVSlideGrouping>
  </array>
  <array rvXMLIvarName="arrangements" />
</RVPresentationDocument>`;
}

// Helper function to extract slide content
function extractSlideContent(slide: SlideSection, index: number): string {
  let slideContent = '';

  if (slide.content && typeof slide.content === 'string') {
    slideContent = slide.content;

    // If it contains HTML, extract text content
    if (slideContent.includes('<') || slideContent.includes('&')) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = slideContent;
        const extractedText = tempDiv.textContent || tempDiv.innerText || '';
        if (extractedText && extractedText.trim().length > 0) {
          slideContent = extractedText;
        }
      } catch (htmlError) {
        // Strip HTML tags manually as fallback
        slideContent = slideContent.replace(/<[^>]*>/g, '');
      }
    }

    // Clean up the content
    slideContent = slideContent
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Ensure we have some content
  if (!slideContent || slideContent.length === 0) {
    slideContent = slide.title || `Slide ${index + 1}`;
  }

  // Create the final slide text
  let finalSlideText = slideContent;
  if (slide.title && slide.title.trim() && slide.title.trim() !== slideContent.trim()) {
    finalSlideText = `${slide.title.trim()}\n\n${slideContent}`;
  }

  return finalSlideText;
}

// Helper function to get alignment value
function getAlignmentValue(alignment: string): number {
  switch (alignment) {
    case 'left': return 0;
    case 'right': return 2;
    case 'center':
    default: return 1;
  }
}

// Create improved RTF data specifically for ProPresenter
function createProPresenterRTFData(content: string, textColor: string, fontFamily: string, fontSize: number, alignment: number, lineHeight: number): string {
  try {
    console.log(`Creating RTF for: "${content?.substring(0, 50)}..."`);

    // Validate and clean inputs
    if (!content || typeof content !== 'string' || content.trim().length === 0) {
      content = 'Sample Text';
    }

    // Clean content
    let cleanContent = content
      .replace(/&nbsp;/g, ' ')
      .replace(/<br\s*\/?>/gi, '\\line ')
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleanContent) {
      cleanContent = 'Sample Text';
    }

    // Parse color safely
    let r = 0, g = 0, b = 0;
    if (textColor && textColor.startsWith('#') && textColor.length >= 7) {
      try {
        r = parseInt(textColor.slice(1, 3), 16) || 0;
        g = parseInt(textColor.slice(3, 5), 16) || 0;
        b = parseInt(textColor.slice(5, 7), 16) || 0;
      } catch (e) {
        r = g = b = 0;
      }
    }

    // Clean font family
    const cleanFontFamily = (fontFamily || 'Arial')
      .replace(/['"]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim() || 'Arial';

    // Validate font size
    fontSize = (fontSize && fontSize > 0) ? Math.round(fontSize) : 24;

    // Escape RTF special characters properly
    let escapedContent = cleanContent
      .replace(/\\/g, '\\\\')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/\n/g, '\\line ')
      .replace(/\r/g, '')
      .replace(/[\u0080-\uFFFF]/g, function(match) {
        const code = match.charCodeAt(0);
        return '\\u' + code + '?';
      });

    // Alignment codes
    let alignmentCode = '\\qc'; // center
    if (alignment === 0) alignmentCode = '\\ql'; // left
    else if (alignment === 2) alignmentCode = '\\qr'; // right

    // Create proper RTF structure for ProPresenter
    const rtfContent = `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss\\fcharset0 ${cleanFontFamily};}}{\\colortbl;\\red0\\green0\\blue0;\\red${r}\\green${g}\\blue${b};}\\f0\\fs${fontSize * 2}\\cf2${alignmentCode} ${escapedContent}\\par}`;

    console.log(`RTF created: ${rtfContent.length} chars`);

    // Base64 encode with error handling
    try {
      const encoded = btoa(unescape(encodeURIComponent(rtfContent)));
      if (encoded.length < 20) {
        throw new Error('Encoded RTF too short');
      }
      return encoded;
    } catch (encodingError) {
      console.error('RTF encoding failed:', encodingError);
      return createSimpleRTFData(cleanContent);
    }
  } catch (error) {
    console.error('Critical error in createProPresenterRTFData:', error);
    return createSimpleRTFData(content || 'Error');
  }
}

// Simple fallback RTF data creator
function createSimpleRTFData(content: string): string {
  try {
    // Ultra-simple RTF that should always work
    const cleanContent = (content || 'Sample Text')
      .replace(/[{}\\]/g, '') // Remove RTF special characters
      .replace(/[^\x20-\x7E\s]/g, '?') // Replace non-ASCII with ?
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim() || 'Sample Text';

    // Create minimal RTF structure
    const simpleRtf = `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss Arial;}}\\f0\\fs48\\qc ${cleanContent}\\par}`;

    console.log(`Simple RTF created: "${simpleRtf}"`);

    // Encode with better error handling
    try {
      const encoded = btoa(simpleRtf);
      console.log(`Simple RTF encoded: ${encoded.length} chars`);
      return encoded;
    } catch (e) {
      console.error('Simple RTF encoding failed, using hardcoded fallback');
      // Return base64 of guaranteed working RTF
      return 'e1xydGYxXGFuc2lcZGVmZjB7XGZvbnR0Ymx7XGYwXGZzd2lzcyBBcmlhbDt9fVxmMFxmczQ4XHFjIFNhbXBsZSBUZXh0XHBhcn0=';
    }
  } catch (error) {
    console.error('Error in createSimpleRTFData:', error);
    // Return guaranteed working base64 RTF
    return 'e1xydGYxXGFuc2lcZGVmZjB7XGZvbnR0Ymx7XGYwXGZzd2lzcyBBcmlhbDt9fVxmMFxmczQ4XHFjIFNhbXBsZSBUZXh0XHBhcn0=';
  }
}

// Create position rect for ProPresenter
function createPositionRect(): string {
  return "0 0 1 1";
}

// Helper function to create ProPresenter cues file
function createProPresenterCuesFile(slides: SlideSection[], title: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>title</key>
  <string>${escapeXml(title)}</string>
  <key>slides</key>
  <array>
    ${slides.map((slide, index) => `
    <dict>
      <key>title</key>
      <string>${escapeXml(slide.title || `Slide ${index + 1}`)}</string>
      <key>filename</key>
      <string>slide_${index + 1}.pro6</string>
      <key>slideNumber</key>
      <integer>${index + 1}</integer>
    </dict>`).join('')}
  </array>
</dict>
</plist>`;
}

// Helper function to generate a UUID
function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Helper function to escape XML special characters
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Helper function to convert hex color to RGBA format (0-1 range) for ProPresenter
function hexToRgba(hex: string): string {
  // Default to white background if invalid hex
  if (!hex || !/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    return '1 1 1 1';
  }

  // Remove # if present
  hex = hex.replace('#', '');

  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  // Parse RGB components
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Ensure values are valid numbers
  const rValue = isNaN(r) ? 1 : r;
  const gValue = isNaN(g) ? 1 : g;
  const bValue = isNaN(b) ? 1 : b;

  return `${rValue.toFixed(6)} ${gValue.toFixed(6)} ${bValue.toFixed(6)} 1`;
}

// Helper function to download an image and convert it to base64
async function downloadImageAsBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return null;

    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result); // Return the full data URL
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error downloading image:', error);
    return null;
  }
}

// Helper function to create a thumbnail for ProPresenter
async function createSlideThumbnail(slide: SlideSection): Promise<Blob> {
  // Create a temporary div for the slide
  const tempDiv = document.createElement('div');
  document.body.appendChild(tempDiv);

  // Set up the slide with 16:9 aspect ratio
  tempDiv.style.width = '320px';
  tempDiv.style.height = '180px';
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.backgroundColor = slide.style?.backgroundColor || '#FFFFFF';

  if (slide.style?.backgroundImage) {
    tempDiv.style.backgroundImage = `url(${slide.style.backgroundImage})`;
    tempDiv.style.backgroundSize = 'cover';
    tempDiv.style.backgroundPosition = 'center';
  }

  // Create content element
  const contentDiv = document.createElement('div');
  contentDiv.textContent = htmlToText(slide.content);
  contentDiv.style.fontFamily = slide.style?.fontFamily || 'Arial';
  contentDiv.style.fontSize = '12px';
  contentDiv.style.color = slide.style?.textColor || '#1C2B39';
  contentDiv.style.textAlign = slide.style?.alignment || 'center';
  contentDiv.style.fontWeight = slide.style?.bold ? 'bold' : 'normal';
  contentDiv.style.fontStyle = slide.style?.italic ? 'italic' : 'normal';
  contentDiv.style.textDecoration = slide.style?.underline ? 'underline' : 'none';
  contentDiv.style.padding = '10px';
  contentDiv.style.lineHeight = String(slide.style?.lineHeight || 1.5);

  // Position content in the middle
  contentDiv.style.position = 'absolute';
  contentDiv.style.top = '0';
  contentDiv.style.left = '0';
  contentDiv.style.right = '0';
  contentDiv.style.bottom = '0';
  contentDiv.style.display = 'flex';
  contentDiv.style.alignItems = 'center';
  contentDiv.style.justifyContent = slide.style?.alignment === 'left' ? 'flex-start' :
                                   slide.style?.alignment === 'right' ? 'flex-end' : 'center';

  tempDiv.appendChild(contentDiv);

  try {
    // Use html2canvas to create an image
    const canvas = await html2canvas(tempDiv, {
      width: 320,
      height: 180,
      scale: 1
    });

    // Convert canvas to JPEG
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create thumbnail blob'));
        }
      }, 'image/jpeg', 0.8);
    });
  } finally {
    // Clean up
    document.body.removeChild(tempDiv);
  }
}

// Function to load fonts and ensure they're available
async function loadCustomFonts(fontFamily: string): Promise<void> {
  if (!fontFamily || fontFamily === 'Arial' || fontFamily === 'Times New Roman' || fontFamily === 'Helvetica') {
    return; // Skip loading for system fonts
  }

  // Extract the primary font name from font stacks
  const primaryFont = fontFamily.split(',')[0].trim().replace(/['"`]/g, '');

  try {
    // Check if font is already loaded
    if (document.fonts && document.fonts.check) {
      const isFontLoaded = document.fonts.check(`16px "${primaryFont}"`);
      if (isFontLoaded) {
        return;
      }
    }

    // Add Google Fonts link if it's a web font
    const googleFonts = [
      'Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Oswald', 'Raleway', 
      'Merriweather', 'Playfair Display', 'Ubuntu', 'Poppins', 'Nunito',
      'Source Sans Pro', 'PT Sans', 'Lora', 'Inter', 'Crimson Text',
      'Libre Baskerville', 'Fira Sans', 'Work Sans', 'IBM Plex Sans'
    ];
    
    if (googleFonts.includes(primaryFont)) {
      // Remove existing font links to avoid duplicates
      const existingLinks = document.querySelectorAll(`link[href*="${encodeURIComponent(primaryFont)}"]`);
      existingLinks.forEach(link => link.remove());

      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(primaryFont)}:wght@400;700&display=swap`;
      document.head.appendChild(linkElement);

      // Wait for font to load with better promise handling
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn(`Font loading timeout for ${primaryFont}`);
          resolve(null);
        }, 5000);

        linkElement.onload = () => {
          clearTimeout(timeout);
          resolve(null);
        };
        linkElement.onerror = () => {
          clearTimeout(timeout);
          console.warn(`Failed to load font link for ${primaryFont}`);
          resolve(null);
        };
      });
    }

    // Try to load the font using CSS Font Loading API
    if ('fonts' in document && document.fonts.load) {
      try {
        await document.fonts.load(`16px "${primaryFont}"`);
        await document.fonts.ready;
      } catch (fontError) {
        console.warn(`Font loading API failed for ${primaryFont}:`, fontError);
      }
    }

    // Additional wait to ensure font rendering
    await new Promise(resolve => setTimeout(resolve, 1500));
  } catch (error) {
    console.warn(`Failed to load custom font "${primaryFont}":`, error);
  }
}

// Function to create image slides using the same approach as preview window
async function createImageSlides(
  slideSections: SlideSection[],
  title: string
): Promise<Blob> {
  // Create a zip file with PNG images
  const zip = new JSZip();

  // Pre-load all unique fonts used in slides
  const uniqueFonts = new Set<string>();
  slideSections.forEach(section => {
    if (section.style?.fontFamily) {
      uniqueFonts.add(section.style.fontFamily);
    }
  });

  // Load all custom fonts before processing slides
  console.log('Loading custom fonts for image export...');
  for (const fontFamily of uniqueFonts) {
    await loadCustomFonts(fontFamily);
  }

  // Add Google Fonts CSS to document head if not already present
  if (!document.querySelector('#google-fonts-style')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'google-fonts-style';
    styleElement.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&family=Roboto:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700&family=Oswald:wght@400;700&family=Raleway:wght@400;700&family=Merriweather:wght@400;700&family=Playfair+Display:wght@400;700&family=Ubuntu:wght@400;700&family=Poppins:wght@400;700&family=Nunito:wght@400;700&family=Source+Sans+Pro:wght@400;700&family=PT+Sans:wght@400;700&family=Lora:wght@400;700&family=Inter:wght@400;700&family=Crimson+Text:wght@400;700&family=Libre+Baskerville:wght@400;700&family=Fira+Sans:wght@400;700&family=Work+Sans:wght@400;700&family=IBM+Plex+Sans:wght@400;700&display=swap');
      * {
        text-rendering: optimizeLegibility !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
    `;
    document.head.appendChild(styleElement);
  }

  // Wait for fonts to load
  await new Promise(resolve => setTimeout(resolve, 2000));

  // Create each slide as a PNG using the same structure as SlidePreview
  for (let i = 0; i < slideSections.length; i++) {
    const section = slideSections[i];

    // Create a temporary container that mimics the preview window structure
    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    // Set up the container to be positioned off-screen but visible for rendering
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '1920px';
    tempContainer.style.height = '1080px';
    tempContainer.style.zIndex = '-1000';

    // Create the slide div that matches the preview structure
    const slideDiv = document.createElement('div');
    slideDiv.className = 'w-full aspect-video rounded-lg shadow-lg flex flex-col items-center justify-center p-8 text-center';
    
    // Apply the same styling as the preview
    slideDiv.style.width = '1920px';
    slideDiv.style.height = '1080px';
    slideDiv.style.backgroundColor = section.style?.backgroundColor || '#FFFFFF';
    slideDiv.style.color = section.style?.textColor || '#1C2B39';
    slideDiv.style.fontFamily = section.style?.fontFamily || 'Arial';
    slideDiv.style.padding = `${(section.style?.padding || 32) * 2.5}px`; // Scale padding for Full HD
    slideDiv.style.display = 'flex';
    slideDiv.style.flexDirection = 'column';
    slideDiv.style.alignItems = 'center';
    slideDiv.style.justifyContent = 'center';
    slideDiv.style.textAlign = 'center';
    slideDiv.style.borderRadius = '0'; // Remove border radius for export
    slideDiv.style.boxShadow = 'none'; // Remove shadow for export

    if (section.style?.backgroundImage) {
      slideDiv.style.backgroundImage = `url(${section.style.backgroundImage})`;
      slideDiv.style.backgroundSize = 'cover';
      slideDiv.style.backgroundPosition = 'center';
      slideDiv.style.backgroundRepeat = 'no-repeat';
    }

    // Add title if present (matches SlidePreview structure exactly)
    if (section.title) {
      const titleElement = document.createElement('h1');
      titleElement.className = 'mb-4';
      titleElement.textContent = section.title;
      // Scale font size for Full HD resolution (1920x1080) to match preview proportions
      // Preview uses CSS sizing, so we need to scale up for actual pixel dimensions
      const baseFontSize = section.style?.fontSize || 24;
      const titleFontSize = Math.round(baseFontSize * 1.2 * 2.5); // Scale up for Full HD and title emphasis
      titleElement.style.fontSize = `${titleFontSize}px`;
      titleElement.style.fontWeight = section.style?.bold ? 'bold' : 'bold';
      titleElement.style.fontStyle = section.style?.italic ? 'italic' : 'normal';
      titleElement.style.textDecoration = section.style?.underline ? 'underline' : 'none';
      titleElement.style.textAlign = section.style?.alignment || 'center';
      titleElement.style.lineHeight = String(section.style?.lineHeight || 1.5);
      titleElement.style.marginBottom = '40px'; // Scale margin too
      titleElement.style.fontFamily = section.style?.fontFamily || 'Arial';
      console.log(`Title font size for slide ${i + 1}: ${titleFontSize}px (base: ${baseFontSize}px, scaled for 1920x1080)`);
      slideDiv.appendChild(titleElement);
    }

    // Add content (matches SlidePreview structure exactly)
    const contentElement = document.createElement('div');
    contentElement.textContent = section.content;
    // Scale content font size for Full HD resolution to match preview proportions
    const baseFontSize = section.style?.fontSize || 24;
    const contentFontSize = Math.round(baseFontSize * 2.5); // Scale up for Full HD
    contentElement.style.fontSize = `${contentFontSize}px`;
    contentElement.style.fontWeight = section.style?.bold ? 'bold' : 'normal';
    contentElement.style.fontStyle = section.style?.italic ? 'italic' : 'normal';
    contentElement.style.textDecoration = section.style?.underline ? 'underline' : 'none';
    contentElement.style.textAlign = section.style?.alignment || 'center';
    contentElement.style.lineHeight = String(section.style?.lineHeight || 1.5);
    contentElement.style.maxWidth = '90%';
    contentElement.style.fontFamily = section.style?.fontFamily || 'Arial';
    console.log(`Content font size for slide ${i + 1}: ${contentFontSize}px (base: ${baseFontSize}px, scaled for 1920x1080)`);
    slideDiv.appendChild(contentElement);

    tempContainer.appendChild(slideDiv);

    // Wait for rendering and font loading
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // Use html2canvas with optimized settings for 16:9 at 72 DPI
      const canvas = await html2canvas(slideDiv, {
        width: 1920,
        height: 1080,
        scale: 1, // 72 DPI equivalent - reduces file size significantly
        useCORS: true,
        allowTaint: false,
        backgroundColor: section.style?.backgroundColor || '#FFFFFF',
        logging: false,
        removeContainer: true,
        imageTimeout: 15000,
        pixelRatio: 1, // Ensure consistent DPI
        onclone: (clonedDoc, element) => {
          // Ensure all styles are preserved in the clone
          const clonedSlide = element as HTMLElement;
          clonedSlide.style.borderRadius = '0';
          clonedSlide.style.boxShadow = 'none';
          
          // Add font imports to cloned document
          const fontStyle = clonedDoc.createElement('style');
          fontStyle.textContent = `
            @import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(section.style?.fontFamily || 'Arial')}:wght@400;700&display=swap');
            * {
              text-rendering: optimizeLegibility !important;
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
            }
          `;
          clonedDoc.head.appendChild(fontStyle);
        }
      });

      // Verify canvas has content
      if (canvas.width > 0 && canvas.height > 0) {
        // Use JPEG with 85% quality for smaller file sizes while maintaining good quality
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const base64Data = dataUrl.split(',')[1];
        
        if (base64Data && base64Data.length > 500) { // Lower threshold for compressed JPEG images
          zip.file(`slide_${i + 1}.jpg`, base64Data, { base64: true });
          console.log(`Generated slide ${i + 1} successfully (${Math.round(base64Data.length / 1024)}KB)`);
        } else {
          throw new Error('Generated canvas appears to be empty');
        }
      } else {
        throw new Error('Canvas has invalid dimensions');
      }

    } catch (error) {
      console.error(`Error creating slide ${i + 1}:`, error);
      
      // Create a fallback text-based image description
      const fallbackContent = `
Slide ${i + 1}: ${section.title || ''}

${section.content}

Background: ${section.style?.backgroundColor || '#FFFFFF'}
Text Color: ${section.style?.textColor || '#1C2B39'}
Font: ${section.style?.fontFamily || 'Arial'}
Font Size: ${section.style?.fontSize || 24}px
      `;
      zip.file(`slide_${i + 1}_fallback.txt`, fallbackContent);
    }

    // Clean up
    document.body.removeChild(tempContainer);
  }

  // Add metadata file
  const fontInfo = Array.from(uniqueFonts).map(font => `Font: ${font}`).join('\n');
  zip.file("metadata.txt", `Title: ${title}\nCreated with SermonBuild\nSlides: ${slideSections.length}\nExport Type: Optimized JPEG images (16:9, 72 DPI)\nResolution: 1920x1080\nCompression: 85% quality\n${fontInfo}`);

  console.log(`Image export complete with ${uniqueFonts.size} custom fonts - optimized for smaller file sizes`);
  return await zip.generateAsync({ 
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: {
      level: 6 // Good balance between size and speed
    }
  });
}

// Function to create ProPresenter 7 presentation (improved format)
async function createProPresenter7(
  slideSections: SlideSection[],
  title: string
): Promise<Blob> {
  // Validate input
  if (!slideSections || slideSections.length === 0) {
    throw new Error('No slides provided for ProPresenter 7 export');
  }

  if (!title || title.trim() === '') {
    title = 'Untitled Presentation';
  }

  console.log(`Creating ProPresenter 7 presentation with ${slideSections.length} slides`);

  // Create ProPresenter 7 format with improved text handling
  const presentationUuid = generateUuid();
  const documentCreationDate = new Date().toISOString();

  // Create slides for ProPresenter 7
  const slidesXml = slideSections.map((slide, index) => {
    try {
      const slideUuid = generateUuid();
      const textElementUuid = generateUuid();
      const backgroundColor = hexToRgba(slide.style?.backgroundColor || '#FFFFFF');
      const textColor = slide.style?.textColor || '#1C2B39';
      const fontSize = slide.style?.fontSize || 24;
      const alignment = slide.style?.alignment || 'center';

      // Ensure we have content
      let slideContent = '';
      if (slide.content && slide.content.trim()) {
        slideContent = slide.content.trim();
      } else {
        slideContent = `Slide ${index + 1}`;
      }

      // Combine title and content more carefully
      let slideText = '';
      if (slide.title && slide.title.trim() && slide.content && slide.content.trim()) {
        slideText = `${slide.title.trim()}\n\n${slideContent}`;
      } else if (slide.title && slide.title.trim()) {
        slideText = slide.title.trim();
      } else if (slideContent) {
        slideText = slideContent;
      } else {
        slideText = `Slide ${index + 1}`;
      }

      console.log(`Processing slide ${index + 1}: "${slideText.substring(0, 50)}..."`);

      const rtfData = createProPresenterRTFData(
        slideText,
        textColor,
        slide.style?.fontFamily || 'Arial',
        fontSize,
        alignment === 'left' ? 0 : alignment === 'right' ? 2 : 1,
        slide.style?.lineHeight || 1.5
      );

      if (!rtfData) {
        throw new Error(`Failed to create RTF data for slide ${index + 1}`);
      }

      const slideLabel = escapeXml(slide.title || `Slide ${index + 1}`);

      return `    <RVDisplaySlide backgroundColor="${backgroundColor}" enabled="1" highlightColor="0 0 0 0" hotKey="" label="${slideLabel}" notes="" slideType="1" sort_index="${index}" UUID="${slideUuid}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="${index}">
      <cues rvXMLIvarName="cues"></cues>
      <displayElements rvXMLIvarName="displayElements">
        <RVTextElement displayDelay="0" displayName="Default" locked="0" persistent="0" typeID="0" fromTemplate="0" bezelRadius="0" drawingFill="0" drawingShadow="0" drawingStroke="0" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="0" verticalAlignment="1" RTFData="${rtfData}" revealType="0" UUID="${textElementUuid}" serialization-array-index="0">
          <stroke rvXMLIvarName="stroke">
            <RVStrokeItem strokeColor="0 0 0 1" strokeWidth="1"></RVStrokeItem>
          </stroke>
          <shadow rvXMLIvarName="shadow">
            <RVShadowItem shadowColor="0 0 0 0.5" shadowOffset="{0, 0}" shadowBlurRadius="0" shadowAngle="0"></RVShadowItem>
          </shadow>
          <position rvXMLIvarName="position">
            <RVRect3D x="0.05" y="0.05" z="0" width="0.9" height="0.9"></RVRect3D>
          </position>
        </RVTextElement>
      </displayElements>
      <arrangements rvXMLIvarName="arrangements"></arrangements>
    </RVDisplaySlide>`;
    } catch (error) {
      console.error(`Error processing slide ${index + 1}:`, error);
      // Create a fallback slide
      const slideUuid = generateUuid();
      const textElementUuid = generateUuid();
      const fallbackText = `Error in slide ${index + 1}: ${slide.content?.substring(0, 100) || 'No content'}`;
      const rtfData = createSimpleRTFData(fallbackText);

      return `    <RVDisplaySlide backgroundColor="1 1 1 1" enabled="1" highlightColor="0 0 0 0" hotKey="" label="Slide ${index + 1}" notes="" slideType="1" sort_index="${index}" UUID="${slideUuid}" drawingBackgroundColor="0 0 0 0" chordChartPath="" serialization-array-index="${index}">
      <cues rvXMLIvarName="cues"></cues>
      <displayElements rvXMLIvarName="displayElements">
        <RVTextElement displayDelay="0" displayName="Default" locked="0" persistent="0" typeID="0" fromTemplate="0" bezelRadius="0" drawingFill="0" drawingShadow="0" drawingStroke="0" fillColor="1 1 1 1" rotation="0" source="" adjustsHeightToFit="0" verticalAlignment="1" RTFData="${rtfData}" revealType="0" UUID="${textElementUuid}" serialization-array-index="0">
          <stroke rvXMLIvarName="stroke">
            <RVStrokeItem strokeColor="0 0 0 1" strokeWidth="1"></RVStrokeItem>
          </stroke>
          <shadow rvXMLIvarName="shadow">
            <RVShadowItem shadowColor="0 0 0 0.5" shadowOffset="{0, 0}" shadowBlurRadius="0" shadowAngle="0"></RVShadowItem>
          </shadow>
          <position rvXMLIvarName="position">
            <RVRect3D x="0.05" y="0.05" z="0" width="0.9" height="0.9"></RVRect3D>
          </position>
        </RVTextElement>
      </displayElements>
      <arrangements rvXMLIvarName="arrangements"></arrangements>
    </RVDisplaySlide>`;
    }
  }).join('\n');

  // Validate we have slide content
  if (!slidesXml || slidesXml.trim() === '') {
    throw new Error('Failed to generate any slide content for ProPresenter 7');
  }

  const escapedTitle = escapeXml(title);

  const proPresenter7Document = `<?xml version="1.0" encoding="UTF-8"?>
<RVPresentationDocument height="1080" width="1920" versionNumber="700" docType="0" creatorCode="1349676880" lastDateUsed="${documentCreationDate}" usedCount="0" category="Sermon" resourcesDirectory="" backgroundColor="0 0 0 1" drawingBackgroundColor="0 0 0 0" notes="" artist="" author="SermonBuild" album="" title="${escapedTitle}" CCLIDisplay="0" CCLIArtistCredits="" CCLISongTitle="" CCLIPublisher="" CCLICopyrightYear="" CCLISongNumber="" CCLILicenseNumber="" chordChartPath="" lastModifiedDate="${documentCreationDate}" UUID="${presentationUuid}">
  <timeline timeOffset="0" selectedMediaTrackIndex="-1" unitOfMeasure="60" duration="0" loop="0" rvXMLIvarName="timeline">
    <timeCues rvXMLIvarName="timeCues"></timeCues>
    <mediaTracks rvXMLIvarName="mediaTracks"></mediaTracks>
  </timeline>
  <groups rvXMLIvarName="groups">
    <RVSlideGrouping name="${escapedTitle}" uuid="${presentationUuid}" color="0 0 0 0" serialization-array-index="0">
      <slides rvXMLIvarName="slides">
${slidesXml}
      </slides>
    </RVSlideGrouping>
  </groups>
  <arrangements rvXMLIvarName="arrangements"></arrangements>
</RVPresentationDocument>`;

  console.log(`ProPresenter 7 document created successfully. Size: ${proPresenter7Document.length} characters`);

  return new Blob([proPresenter7Document], { type: 'application/xml' });
}

// Function to cleanup temporary slide images
export async function cleanupSlideImages(slideSections: SlideSection[]): Promise<void> {
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    );

    const imagesToDelete: string[] = [];

    slideSections.forEach(section => {
      if (section.style?.backgroundImage) {
        const imageUrl = section.style.backgroundImage;
        // Only clean up images from our storage
        if (imageUrl.includes('supabase') && imageUrl.includes('slide-images/')) {
          // Extract the file path from the URL
          const urlParts = imageUrl.split('/');
          const bucketIndex = urlParts.findIndex(part => part === 'sermon-media');
          if (bucketIndex !== -1) {
            const filePath = urlParts.slice(bucketIndex + 1).join('/');
            imagesToDelete.push(filePath);
          }
        }
      }
    });

    if (imagesToDelete.length > 0) {
      const { error } = await supabase.storage
        .from('sermon-media')
        .remove(imagesToDelete);

      if (error) {
        console.warn('Error cleaning up slide images:', error);
      } else {
        console.log(`Cleaned up ${imagesToDelete.length} temporary slide images`);
      }
    }
  } catch (error) {
    console.warn('Error during image cleanup:', error);
  }
}

// Main function to export slides in different formats
export async function exportSlides(
  slideSections: SlideSection[],
  title: string,
  format: 'pptx' | 'images'
): Promise<Blob> {
  const sanitizedTitle = title.replace(/[^a-z0-9]/gi, '-').toLowerCase();

  console.log(`Exporting slides in format: ${format}`);
  console.log(`Title: ${title}`);
  console.log(`Number of slides: ${slideSections.length}`);
  console.log('Slide sections:', slideSections.map((s, i) => ({
    index: i,
    title: s.title,
    hasContent: !!s.content,
    contentLength: s.content?.length
  })));

  switch (format) {
    case 'pptx': {
      console.log('Creating PowerPoint presentation...');
      const blob = await createPowerPoint(slideSections, title);
      saveAs(blob, `${sanitizedTitle}.pptx`);
      return blob;
    }
    case 'images': {
      console.log('Creating slide images...');
      const blob = await createImageSlides(slideSections, title);
      saveAs(blob, `${sanitizedTitle}-images.zip`);
      return blob;
    }
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
}